import React, { FC, useState, useEffect, useRef, useContext } from "react";
import useProjectStore, { DistanceUnit } from "common/store/ProjectStore";
import theme from "common/theme/theme";
import { Menu, MenuItem } from "@mui/material";
import { FeetMetersConversion } from "common/constants/GlobalValues";
import { LanguageContext } from "common/contexts/LanguageContext";
import { SharedProjectContext } from "components/project/Project";

export enum ObstructionType {
  Rectangle = 1,
  Ellipse = 2,
}

enum RectSide {
  Top = 1,
  Bottom = 2,
  Left = 3,
  Right = 4,
}

interface IProps {
  instanceIndex: number;
  instanceID: number;
  initialX: number;
  initialY: number;
  handlePropertiesRequest: () => void;
  setObstructionUpdated: (obstructionUpdated: { updated: boolean; index: number }) => void;
  plotImageTransform: number;
}

const DroppedObstruction: FC<IProps> = (props) => {
  const {instanceIndex, instanceID, initialX, initialY, handlePropertiesRequest, setObstructionUpdated, plotImageTransform} = props;

  const isInitialRender = useRef(true);
  const firstUpdateZoom = useRef(true);
  const firstUpdateMeasurement = useRef(true);
  const firstUpdateScaling = useRef(true);

  //Center coords get saved in database
  const [centerX, setCenterX] = useState<number>();
  const [centerY, setCenterY] = useState<number>();

  //width in pixels (not distance) at 100% zoom
  const [width, setWidth] = useState<number>();
  const [length, setLength] = useState<number>();

  const [isFocused, setIsFocused] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dotSize = 8; //resize dot size
  const [side, setSide] = useState<RectSide>();
  const [limit, setLimit] = useState<number>();
  const [resized, setResized] = useState<boolean>(false);

  const [calculatedX, setCalculatedX] = useState<number>();
  const [calculatedY, setCalculatedY] = useState<number>();

  // calculated width in pixels, horizontal -- (x axis)
  const [calculatedWidth, setCalculatedWidth] = useState<number>();
  // calculated length in pixels, vertical | (y axis)
  const [calculatedLength, setCalculatedLength] = useState<number>();

  // location (x,y) on the draggable icon that the cursor is located
  const [cursorX, setCursorX] = useState<number>(0);
  const [cursorY, setCursorY] = useState<number>(0);

  const [isEllipse, setIsEllipse] = useState<boolean>(false);
  const [measurementUnit, setMeasurementUnit] = useState<number>();
  const [openObstructionMenu, setOpenObstructionMenu] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const { zoomPercent, obstructionInstances, scalingFactor, updateObstructionInstance, distanceID, deleteObstructionInstance } = useProjectStore();
  const { getString } = useContext(LanguageContext);
  const { bladesViewAvailable } = useContext(SharedProjectContext);

  const transformViewportCoords = () => {
    var newW = width! * plotImageTransform;
    var newL = length! * plotImageTransform;
    setCalculatedWidth(newW);
    setCalculatedLength(newL);
    setCenterX(initialX * plotImageTransform);
    setCenterY(initialY * plotImageTransform);
    setCalculatedX(initialX * plotImageTransform - (newW/2));
    setCalculatedY(initialY * plotImageTransform - (newL/2));
  };

  useEffect(() => {
    transformViewportCoords();
  }, [plotImageTransform]);

  //useEffect (onMount) to handle position on initial drop/reload/from database
  useEffect(() => {
    if(isInitialRender.current) {
      var w = obstructionInstances[instanceIndex].width! * scalingFactor! * plotImageTransform;
      var l = obstructionInstances[instanceIndex].length! * scalingFactor! * plotImageTransform;
      setCenterX(initialX * plotImageTransform);
      setCenterY(initialY * plotImageTransform);
      setCalculatedX(initialX * plotImageTransform - (w/2));
      setCalculatedY(initialY * plotImageTransform - (l/2));
      setWidth(w / plotImageTransform);
      setLength(l / plotImageTransform);
      setCalculatedWidth(w);
      setCalculatedLength(l);

      if(obstructionInstances[instanceIndex].obstructionTypeID === ObstructionType.Ellipse) {
        setIsEllipse(true);
      }

      isInitialRender.current = false;
    }
  }, []);

  //on Drag
  function handleOnDrag(e: React.DragEvent) {
    var boxElement = document.getElementById("plot-plan-view-port");
    if (boxElement && calculatedX && calculatedY) {
      setCursorX(e.clientX);
      setCursorY(e.clientY);
    }
  }

  //onDrop
  function handleOnDrop(e: React.DragEvent) {
    var boxElement = document.getElementById("plot-plan-view-port");
    if (boxElement) {
      var box = boxElement.getBoundingClientRect();
      var boxLeft = box.left;
      //if devicesBox is open, get the box width and add it to box left
      if (bladesViewAvailable) {
        var bladeElement = document.getElementById("device-blade-overlay");
        if (bladeElement) {
          var bladeBox = bladeElement.getBoundingClientRect();
          boxLeft = boxLeft + bladeBox.width;
        }
      }
      if (box.right > e.clientX && e.clientX > boxLeft && box.bottom > e.clientY && e.clientY > box.top) {
        //adjust X,Y if zoomed in/out (else zoomPercent/100 will equal 1)
        const newCalcX = calculatedX! + (e.clientX - cursorX) / (zoomPercent / 100);
        const newCalcY = calculatedY! + (e.clientY - cursorY) / (zoomPercent / 100);
        setCenterX(newCalcX + (calculatedWidth!/2));
        setCenterY(newCalcY + (calculatedLength!/2));
        setCalculatedX(newCalcX);
        setCalculatedY(newCalcY);
        //update device x and y positions in project store
        updateCoords(
          (newCalcX + (calculatedWidth!/2)) / plotImageTransform,
          (newCalcY + (calculatedLength!/2)) / plotImageTransform,
        );

      } else {
        e.stopPropagation();
      }
    }
  }

  //useEffect on distanceID (does not run on mount)
  useEffect(() => {
    if (firstUpdateMeasurement.current) {
      setMeasurementUnit(distanceID);
      firstUpdateMeasurement.current = false;
    } else {
      var obs ={...obstructionInstances[instanceIndex]};
      if(distanceID !== measurementUnit) {
        if (distanceID === DistanceUnit.Feet) {
          //convert from meters to feet
          obs.height = obs.height! * FeetMetersConversion;
          obs.width = obs.width! * FeetMetersConversion;
          obs.length = obs.length! * FeetMetersConversion;
        } else {
          //convert from feet to meters
          obs.height = obs.height! / FeetMetersConversion;
          obs.width = obs.width! / FeetMetersConversion;
          obs.length = obs.length! / FeetMetersConversion;
        }
        //updateObstructionInstances(networkID, dev);
        updateObstructionInstance(obs.obstructionInstanceID, obs);
        setMeasurementUnit(distanceID)
      }
    }  
  }, [distanceID]);

  //useEffect to handle scalingFactor change
  useEffect(() => {
    if (firstUpdateScaling.current) {
      firstUpdateScaling.current = false;
      return;
    }
    //recalculate everything affected by scaling factor
    var w = obstructionInstances[instanceIndex].width! * scalingFactor! * plotImageTransform;
    var l = obstructionInstances[instanceIndex].length! * scalingFactor! * plotImageTransform;
    setCalculatedX(centerX! - w / 2);
    setCalculatedY(centerY! - l / 2);
    setWidth(w / plotImageTransform);
    setLength(l / plotImageTransform);
    setCalculatedWidth(w);
    setCalculatedLength(l);
  }, [scalingFactor]); // should only trigger on scalingFactor, not on first render

  // handle: right-click to open the device menu
  function openMenu(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    setAnchor(e.currentTarget);
    setOpenObstructionMenu(true);
  }

  //updateCoordsAndSize utility function
  function updateCoordsAndSize(x: number, y: number, w: number, l: number) {
    var obs = { ...obstructionInstances[instanceIndex] };
    obs.xPosition = x;
    obs.yPosition = y;
    obs.width = w / scalingFactor!;
    obs.length = l / scalingFactor!;
    updateObstructionInstance(obs.obstructionInstanceID, obs);
    setObstructionUpdated({updated: true, index: instanceIndex});
  }

  //updateCoords utility function
  function updateCoords(x: number, y: number) {
    var obs = { ...obstructionInstances[instanceIndex] };
    obs.xPosition = x;
    obs.yPosition = y;
    updateObstructionInstance(obs.obstructionInstanceID, obs);
    setObstructionUpdated({updated: true, index: instanceIndex});
  }

  //handle Focus
  // Handler for focus event
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Handler for blur event
  const handleBlur = () => {
    setIsFocused(false);
  };

  //handle onHover
  const handleMouseHover = () => {
    setIsHover(true);
  }

  const handleMouseLeave = () => {
    setIsHover(false);
  }

  // Handle Resizing
  const handleMouseDownTop = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
    setSide(1);
    setLimit(calculatedY! + calculatedLength!);
  };
  const handleMouseDownBottom = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
    setSide(2);
    setLimit(calculatedY!);
  };
  const handleMouseDownLeft = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
    setSide(3);
    setLimit(calculatedX! + calculatedWidth!);
  };
  const handleMouseDownRight = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
    setSide(4);
    setLimit(calculatedX!);
  };  

  const handleDoubleClick = () => {
      handlePropertiesRequest();
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !ref.current) return;
    var boxElement = document.getElementById("drop-box");
    if (boxElement && calculatedX && calculatedY) {
      var box = boxElement.getBoundingClientRect();
      if (limit && side === RectSide.Top) {
        const adjTop = (e.clientY - box.top) / (zoomPercent / 100);
        if (limit > adjTop) { // 3 px because 2 px is the border thickness
          setCalculatedY(adjTop);
          setCalculatedLength(calculatedY! - adjTop + calculatedLength!);
        }
      }
      else if (limit && side === RectSide.Bottom) {
        const adjTop = (e.clientY - box.top) / (zoomPercent / 100);
        if (limit + 3 < adjTop) { //: 3 px because 2 px is the border thickness
          setCalculatedLength(adjTop - calculatedY);
        }
      }
      else if (limit && side === RectSide.Left) {
        const adjLeft = (e.clientX - box.left) / (zoomPercent / 100);
        if (limit > (adjLeft + 3)) { // 3 px because 2 px is the border thickness
          setCalculatedX(adjLeft);
          setCalculatedWidth(calculatedX! - adjLeft + calculatedWidth!);
        }
      }
      else if (limit && side === RectSide.Right) {
        const adjLeft = (e.clientX - box.left) / (zoomPercent / 100);
        if (limit + 3 < adjLeft) { // 3 px because 2 px is the border thickness
          setCalculatedWidth(adjLeft - calculatedX);
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setResized(true);
    setLimit(undefined);
  };

  useEffect(() => {
    if (resized) {
      setResized(false);
      var newW = calculatedWidth! / plotImageTransform; 
      var newL = calculatedLength! / plotImageTransform;
      setCenterX(calculatedX! + ((newW * plotImageTransform) / 2));
      setCenterY(calculatedY! + ((newL * plotImageTransform) / 2));
      setWidth(newW);
      setLength(newL);
      //call updateCoordsAndSize
      updateCoordsAndSize(calculatedX! / (plotImageTransform) + (newW / 2), calculatedY! / (plotImageTransform) + (newL / 2), newW, newL);
    }
  }, [resized, calculatedWidth, calculatedLength]);

  React.useEffect(() => {
    if(isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  return (
    <>
      <div
        ref={ref}
        draggable
        onDragStart={(e) => handleOnDrag(e)}
        onDragEnd={(e) => handleOnDrop(e)}
        style={{
          left: calculatedX,
          top: calculatedY,
          backgroundColor: (isFocused || isHover) ? `${theme.palette.primary.light}` : "transparent",
          border: `2px solid ${theme.palette.primary.dark}`,
          position: "absolute",
          zIndex: 3,
          width: calculatedWidth,
          height: calculatedLength,
          borderRadius: isEllipse ? "50%" : 0,
        }}
        tabIndex={0} // Make the div focusable
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseEnter={handleMouseHover}
        onMouseLeave={handleMouseLeave}
        onContextMenu={(e) => openMenu(e)}
        onDoubleClick={handleDoubleClick}
      />
      {(isFocused || isResizing) && (
        <>
          <div
            className="left-vertical line"
            style={{
              left: calculatedX! - dotSize / 2,
              top: calculatedY! + calculatedLength! / 2 - dotSize / 2,
              position: "absolute",
              backgroundColor: `${theme.palette.primary.light}`,
              width: dotSize,
              height: dotSize,
              zIndex: 4,
              cursor: "ew-resize",
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseDown={handleMouseDownLeft}
          />
          <div
            className="top-horizontal line"
            style={{
              left: calculatedX! + calculatedWidth! / 2 - dotSize / 2,
              top: calculatedY! - dotSize / 2,
              position: "absolute",
              backgroundColor: `${theme.palette.primary.light}`,
              width: dotSize,
              height: dotSize,
              zIndex: 4,
              cursor: "ns-resize",
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseDown={handleMouseDownTop}
          />
          <div
            className="right-vertical line"
            style={{
              left: calculatedX! + calculatedWidth! - dotSize / 2,
              top: calculatedY! + calculatedLength! / 2 - dotSize / 2,
              position: "absolute",
              backgroundColor: `${theme.palette.primary.light}`,
              width: dotSize,
              height: dotSize,
              zIndex: 4,
              cursor: "ew-resize",
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseDown={handleMouseDownRight}
          />
          <div
            className="bottom-horizontal line"
            style={{
              left: calculatedX! + calculatedWidth! / 2 - dotSize / 2,
              top: calculatedY! + calculatedLength! - dotSize / 2,
              position: "absolute",
              backgroundColor: `${theme.palette.primary.light}`,
              width: dotSize,
              height: dotSize,
              zIndex: 4,
              cursor: "ns-resize",
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseDown={handleMouseDownBottom}
          />
        </>
      )}
      <Menu
        open={openObstructionMenu}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setOpenObstructionMenu(false);
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenObstructionMenu(false);
            handlePropertiesRequest();
          }}
        >
          {getString('cmn_properties')}
        </MenuItem>
        <MenuItem
            onClick={() => {
              setOpenObstructionMenu(false);
              deleteObstructionInstance(instanceID);
              setObstructionUpdated({updated: true, index: -1});
            }}
          >
            {getString('cmn_delete')}
          </MenuItem>
      </Menu>
    </>
  );
};

export default DroppedObstruction;
